import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import type { ISpClaimedReward } from '@brushfire/types'
import { RiFileCopyLine } from '@/shared/icons/remix'
import { useToast } from '@/shared/toast'

function formatRedeemedDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

function formatExpiry(expirationDate: string | null | undefined): string {
  if (!expirationDate) return 'No expiration'
  return `Expires ${new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(expirationDate))}`
}

function mediaSrc(value: string): string {
  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`
}

interface Props {
  reward: ISpClaimedReward
}

export function RewardViewDetails({ reward }: Readonly<Props>) {
  const { showToast } = useToast()

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showToast('Copied to clipboard', 'success')
    } catch {
      showToast('Unable to copy', 'error')
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {reward.brandLogoUrl ? (
          <Box
            component="img"
            src={reward.brandLogoUrl}
            alt=""
            sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: '8px', flexShrink: 0 }}
          />
        ) : null}
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0d1b2a' }}>
            {reward.brandName} Gift Card
          </Typography>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#008cd0', mt: 0.5 }}>
            ${reward.amountUsd.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 3 }}>
        Redeemed {formatRedeemedDate(reward.redeemedAt)}
      </Typography>

      {reward.giftCode ? (
        <Box
          sx={{
            bgcolor: '#F1F5F9',
            borderRadius: '10px',
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
              }}
            >
              Gift Card Code
            </Typography>
            <Typography
              sx={{ fontSize: '18px', fontWeight: 700, color: '#0d1b2a', letterSpacing: '2px' }}
              data-testid="reward-view-gift-code"
            >
              {reward.giftCode}
            </Typography>
          </Box>
          <IconButton
            aria-label="Copy gift code"
            onClick={() => void copyText(reward.giftCode ?? '')}
            data-testid="reward-view-copy-code"
            sx={{ flexShrink: 0 }}
          >
            <RiFileCopyLine size={20} aria-hidden />
          </IconButton>
        </Box>
      ) : null}

      {reward.pin ? (
        <Box
          sx={{
            bgcolor: '#F1F5F9',
            borderRadius: '10px',
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
              }}
            >
              PIN
            </Typography>
            <Typography
              sx={{ fontSize: '18px', fontWeight: 700, color: '#0d1b2a', letterSpacing: '2px' }}
              data-testid="reward-view-pin"
            >
              {reward.pin}
            </Typography>
          </Box>
          <IconButton
            aria-label="Copy PIN"
            onClick={() => void copyText(reward.pin ?? '')}
            data-testid="reward-view-copy-pin"
            sx={{ flexShrink: 0 }}
          >
            <RiFileCopyLine size={20} aria-hidden />
          </IconButton>
        </Box>
      ) : null}

      {reward.qrCode ? (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontSize: '11px',
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
            }}
          >
            QR Code
          </Typography>
          <Box
            component="img"
            src={mediaSrc(reward.qrCode)}
            alt="QR code for gift card redemption"
            sx={{ width: 160, height: 160, objectFit: 'contain' }}
            data-testid="reward-view-qr-code"
          />
        </Box>
      ) : null}

      {reward.barcode && !reward.qrCode ? (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontSize: '11px',
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
            }}
          >
            Barcode
          </Typography>
          <Box
            component="img"
            src={mediaSrc(reward.barcode)}
            alt="Barcode for gift card redemption"
            sx={{ maxWidth: '100%', height: 'auto' }}
            data-testid="reward-view-barcode"
          />
        </Box>
      ) : null}

      {reward.rewardLink && !reward.giftCode ? (
        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            component="a"
            href={reward.rewardLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reward-view-redeem-link"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#008cd0',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0076b0', boxShadow: 'none' },
            }}
          >
            Redeem Online
          </Button>
        </Box>
      ) : null}

      {reward.status === 'failed' ? (
        <Typography
          sx={{ fontSize: '14px', fontWeight: 500, color: '#DC2626', mb: 2 }}
          data-testid="rewards-claimed-view-failed"
        >
          Email delivery failed for this reward. Your balance has been restored — try redeeming again
          or contact support with your transaction ID below.
        </Typography>
      ) : null}

      {reward.status === 'ready' && !reward.giftCode && !reward.rewardLink ? (
        <Typography
          sx={{ fontSize: '14px', color: '#475569', mb: 2 }}
          data-testid="rewards-claimed-view-email-delivery"
        >
          {reward.deliveryMethod === 'EMAIL'
            ? 'This reward was sent to your email. Check your inbox for redemption instructions.'
            : 'Your reward is ready. If you do not see a code here, check your email for delivery details.'}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid #E2E8F0',
          pt: 2,
          mt: 2,
        }}
      >
        <Typography sx={{ fontSize: '13px', color: '#64748b' }} data-testid="reward-view-expiry">
          {formatExpiry(reward.expirationDate)}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
          ID: {reward.transactionId}
        </Typography>
      </Box>
    </>
  )
}
